// Marketing helpers (Ordre 21): campaigns and partners. Rules live on the server; this only guides the form and labels the answers.
import { hasPermission } from "./permissions";
import { EXPONENTS, formatMinor, toMinor } from "./finance";

const on = (user) => !!user?.features?.marketing;
export const canSeeMarketing = (user) => on(user) && hasPermission(user, "marketing:view");
export const canSeeMarketingCosts = (user) => canSeeMarketing(user) && hasPermission(user, "marketing:view_costs");
export const canManageCampaigns = (user) => canSeeMarketing(user) && hasPermission(user, "marketing:manage_campaigns");
export const canManagePartners = (user) => canSeeMarketing(user) && hasPermission(user, "marketing:manage_partners");

export const newKey = () => `m-${Math.random().toString(16).slice(2)}${Date.now().toString(16)}`;

export const CAMPAIGN_STATE_LABELS = { draft: "Brouillon", active: "En cours", ended: "Terminée", archived: "Archivée" };
export const CAMPAIGN_STATE_TONES = { draft: "slate", active: "emerald", ended: "sky", archived: "slate" };
export const CAMPAIGN_ACTION_LABELS = { activate: "Lancer", end: "Terminer", reopen: "Rouvrir", archive: "Archiver" };
export const CAMPAIGN_ACTION_HINTS = {
  activate: "La campagne passe « En cours ».",
  end: "La campagne est terminée ; ses résultats restent consultables.",
  reopen: "La campagne repasse « En cours ». Indiquez pourquoi elle est rouverte.",
  archive: "La campagne sort des listes courantes. Son historique est conservé.",
};
export const REASON_REQUIRED_ACTIONS = ["reopen"];
export const PARTNER_STATE_LABELS = { active: "Actif", ended: "Terminé" };
export const PARTNER_TYPES = [
  ["institution", "Institution ou organisme public"], ["pharmacy_group", "Groupement de pharmacies"], ["wholesaler", "Grossiste-répartiteur"],
  ["association", "Association ou ordre professionnel"], ["other", "Autre"],
];
export const partnerTypeLabel = (code) => (PARTNER_TYPES.find(([id]) => id === code) || [])[1] || code;
export const CURRENCIES = Object.keys(EXPONENTS);

export function marketingError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.msg) return detail.msg;
  if (detail?.message) return detail.message;
  if (Array.isArray(detail)) return "Vérifiez les champs saisis.";
  return fallback;
}

const UTM = /^[a-z0-9][a-z0-9_-]{0,63}$/;
export const utmProblem = (value) => (value && !UTM.test(value) ? "minuscules, chiffres, tiret ou soulignement" : "");

export const budgetLine = (currency = "XOF", amount = "") => ({ currency, amount });
export const budgetFromApi = (budget) => Object.entries(budget || {}).map(([currency, minor]) => budgetLine(currency, String(EXPONENTS[currency] ? minor / 10 ** EXPONENTS[currency] : minor)));
export const formatBudget = (budget) => Object.entries(budget || {}).map(([currency, minor]) => formatMinor(minor, currency)).join(" · ");

export function emptyCampaignForm(campaign) {
  return {
    name: campaign?.name || "", channel: campaign?.channel || "", starts_on: campaign?.starts_on || "", ends_on: campaign?.ends_on || "",
    owner_id: campaign?.owner?.id || "", budget: campaign?.budget ? budgetFromApi(campaign.budget) : [], notes: campaign?.notes || "",
    utm_source: campaign?.utm?.source || "", utm_medium: campaign?.utm?.medium || "", utm_campaign: campaign?.utm?.campaign || "",
  };
}

export function campaignProblem(form) {
  if (form.name.trim().length < 3) return "Donnez un nom à la campagne.";
  if (!form.channel) return "Choisissez un canal.";
  if (!form.starts_on) return "Indiquez la date de début.";
  if (form.ends_on && form.ends_on < form.starts_on) return "La fin ne peut pas précéder le début.";
  if (!form.owner_id) return "Choisissez le responsable.";
  for (const key of ["utm_source", "utm_medium", "utm_campaign"]) if (utmProblem(form[key])) return `Paramètre UTM invalide : ${utmProblem(form[key])}.`;
  const seen = new Set();
  for (const line of form.budget) {
    if (toMinor(line.amount, line.currency) === null) return "Chaque budget doit être un montant supérieur à zéro.";
    if (seen.has(line.currency)) return "Une seule ligne de budget par devise.";
    seen.add(line.currency);
  }
  return "";
}

// `withBudget` is false for someone who may edit a campaign but not read its budget: they must not overwrite what they cannot see.
export function campaignPayload(form, withBudget = true) {
  const body = {
    name: form.name.trim(), channel: form.channel, starts_on: form.starts_on, ends_on: form.ends_on || null, owner_id: form.owner_id,
    utm: { source: form.utm_source.trim() || null, medium: form.utm_medium.trim() || null, campaign: form.utm_campaign.trim() || null },
    notes: form.notes.trim() || null,
  };
  if (withBudget) body.budget = Object.fromEntries(form.budget.map((l) => [l.currency, toMinor(l.amount, l.currency)]));
  return body;
}

export function emptyPartnerForm(partner) {
  return {
    name: partner?.name || "", type: partner?.type || "", owner_id: partner?.owner?.id || "", contact_name: partner?.contact_name || "",
    contact_function: partner?.contact_function || "", contact_email: partner?.contact_email || "", contact_phone: partner?.contact_phone || "",
    notes: partner?.notes || "",
  };
}

export function partnerProblem(form) {
  if (form.name.trim().length < 2) return "Donnez le nom de l'organisme.";
  if (!form.type) return "Choisissez le type de partenaire.";
  if (!form.owner_id) return "Choisissez le responsable.";
  if (form.contact_email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.contact_email.trim())) return "Adresse e-mail invalide.";
  return "";
}

export const partnerPayload = (form) => ({
  name: form.name.trim(), type: form.type, owner_id: form.owner_id, contact_name: form.contact_name.trim() || null,
  contact_function: form.contact_function.trim() || null, contact_email: form.contact_email.trim() || null,
  contact_phone: form.contact_phone.trim() || null, notes: form.notes.trim() || null,
});

export const HISTORY_LABELS = {
  campaign_created: "Campagne créée", campaign_updated: "Campagne modifiée", campaign_activate: "Campagne lancée", campaign_end: "Campagne terminée",
  campaign_reopen: "Campagne rouverte", campaign_archive: "Campagne archivée", partner_created: "Partenaire ajouté", partner_updated: "Fiche modifiée",
  partner_ended: "Partenariat terminé", partner_resumed: "Partenariat repris", spend_linked: "Dépense reliée", spend_unlinked: "Dépense détachée",
};

// === Origin of a pharmacy and communication preferences (21-2) ===========================================================
export const PREF_STATE_LABELS = { allowed: "Autorisé", refused: "Refusé", unknown: "Inconnu" };
export const PREF_STATE_TONES = { allowed: "emerald", refused: "red", unknown: "slate" };
export const touchKindLabel = (kind) => ({ campaign: "Campagne", partner: "Partenaire", channel: "Canal" }[kind] || kind);

// One origin among a campaign, a partner or a bare channel: the picker's value is « campaign:ID », « partner:ID » or « channel:CODE ».
export function touchPayload(choice, on, note) {
  const [kind, ref] = (choice || "").split(":");
  const body = kind === "campaign" ? { campaign_id: ref } : kind === "partner" ? { partner_id: ref } : { channel: ref };
  return { ...body, ...(on ? { on } : {}), note: (note || "").trim() || null };
}
export const touchProblem = (choice, on) => {
  if (!choice) return "Choisissez une campagne, un partenaire ou un canal.";
  if (on && on > new Date().toISOString().slice(0, 10)) return "La date ne peut pas être dans le futur.";
  return "";
};

export function emptyPrefForm(preferences) {
  const channels = {};
  for (const code of ["email", "whatsapp", "phone"]) {
    const c = preferences?.channels?.[code] || {};
    channels[code] = { state: c.state || "unknown", basis: c.basis || "", evidence: c.evidence || "", on: c.on || "" };
  }
  return { channels, do_not_contact: !!preferences?.do_not_contact, do_not_contact_reason: preferences?.do_not_contact_reason || "" };
}

export function prefProblem(form) {
  for (const [code, c] of Object.entries(form.channels)) {
    if (c.state === "unknown") continue;
    if (!c.on) return "Indiquez la date de chaque préférence enregistrée.";
    if (c.on > new Date().toISOString().slice(0, 10)) return "Une date ne peut pas être dans le futur.";
    if (c.state === "allowed" && !c.basis) return `Indiquez la base déclarée (${code}).`;
    if (c.state === "allowed" && c.evidence.trim().length < 3) return "Indiquez d'où vient la preuve.";
  }
  if (form.do_not_contact && form.do_not_contact_reason.trim().length < 3) return "Indiquez pourquoi la pharmacie ne doit plus être sollicitée.";
  return "";
}

export function prefPayload(form, version) {
  const channels = {};
  for (const [code, c] of Object.entries(form.channels)) {
    channels[code] = c.state === "unknown" ? { state: "unknown" }
      : { state: c.state, on: c.on, basis: c.state === "allowed" ? c.basis : null, evidence: c.evidence.trim() || null };
  }
  return { expected_version: version || 0, channels, do_not_contact: form.do_not_contact, do_not_contact_reason: form.do_not_contact ? form.do_not_contact_reason.trim() : null };
}

// === Spend and figures (21-3) ====================================================================================================
export const canExportMarketing = (user) => canSeeMarketing(user) && hasPermission(user, "marketing:export");

// { XOF: 750000, EUR: 1000 } → « 750 000 FCFA · 10,00 € ». null values (nothing to divide by) read as « — ».
export const formatMoneyMap = (map) => {
  const parts = Object.entries(map || {}).sort(([a], [b]) => a.localeCompare(b)).map(([c, minor]) => (minor === null ? `— ${c}` : formatMinor(minor, c)));
  return parts.length ? parts.join(" · ") : "";
};
export const formatRate = (rate) => (rate === null || rate === undefined ? "—" : `${(rate * 100).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %`);
export const formatDays = (days) => (days === null || days === undefined ? "—" : `${days.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} j`);

export const isoDay = (date) => date.toISOString().slice(0, 10);
export function defaultPeriod(now = new Date(), days = 90) {
  return { from: isoDay(new Date(now.getTime() - days * 86400000)), to: isoDay(now) };
}
export const periodProblem = ({ from, to }) => (!from || !to ? "Indiquez les deux dates." : from > to ? "Le début doit précéder la fin." : "");

export const SPEND_STATUS_LABELS = { draft: "Brouillon", submitted: "Soumise" };
export const STAGE_LABELS = {
  new_lead: "Nouveau lead", contacted: "Contacté", meeting_scheduled: "Rendez-vous", qualified: "Qualifié", demo_negotiation: "Démo / négociation",
  won: "Gagné", onboarding_in_progress: "Onboarding", active_client: "Client actif", lost: "Perdu",
};
